'use client';

import { useIsUserAuthenticated } from '@/hooks/isUserAuthenticated';
import JobService from '@/services/Job.service';
import UserService, { ApplicationData, CvProgress } from '@/services/User.service';
import applyForThisJobStyles from '@/styles/apply-for-this-job-modal.module.css';
import styles from '@/styles/job-details-card.module.css';
import { Job } from '@/types/job';
import { dateTimeFormat, getRelativeDayFromToday, getRelativeDayLabel } from '@/utils/date';
import { monetaryMask } from '@/utils/monetary-mask';
import Box from '@mui/material/Box';
import Link from '@/components/link-with-utms';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import Loading from '../loading';
import SimpleModal from '../modals/simple-modal';
import { ContainerLinkDetails } from './job-details-card';
import { validWaysOfContact } from './valid-ways-of-contact';
import WaysOfContactList, { prefetchWhatsappMessageInstructions } from './ways-of-contact';

import { getBenefitIcon } from '@/constants/benefits-icon';
import Image from 'next/image';
import Chip from '../pageLayout/chip/chip';
import { AmplitudeService } from '@/services/Amplitude.service';
import GtagService from '@/services/Gtag.service';
import { trackedLink } from '@/utils/utms';
import { Deficiency, UserPersonalData } from '@/types/curriculum';
import { DeficienciesService } from '@/services/Deficiencies.service';
import SelectField from '../form/select-field';
import { maskActivities } from './mask-activities';
import { useUtms } from '@/hooks/useUtms';
import { formatJobTitle } from '@/utils/format-job-title';

interface Props {
  job: Job;
  linkToDetails?: boolean;
}

export default function JobDetailsCardContent({ job, linkToDetails = false }: Props) {
  const {
    activities,
    salary,
    jobAmount,
    benefits,
    tagsBenefits,
    extraInformation,
    weeklyWorkingHours,
    hiringContractTypes,
    companyPresentation,
  } = job;

  const [isModalCompleteCvPCDOpen, setIsModalCompleteCvPCDOpen] = useState(false);
  const [isModalDeficienciesOpen, setIsModalDeficienciesOpen] = useState(false);
  const [deficiencies, setDeficiencies] = useState<Deficiency[]>([]);
  const [newDeficiency, setNewDeficiency] = useState<Deficiency | undefined>(undefined);
  const [personalData, setPersonalData] = useState<UserPersonalData | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [isModalApplySuccessOpen, setIsModalApplySuccessOpen] = useState(false);
  const [cvProgress, setCvProgress] = useState<CvProgress | null>(null);
  const [isModalCvFillingOpen, setModalCvFillingOpen] = useState(false);
  const [{ messageBody, messageTitle }, setMessage] = useState({ messageBody: ``, messageTitle: `` });
  const router = useRouter();
  const utms = useUtms();
  const [hasApplied, setHasApplied] = useState<ApplicationData | null>(null);
  const isLoggedIn = useIsUserAuthenticated();

  const showMessage = ({ title = ``, message = `` }) => {
    if (title || message) {
      setMessage({
        messageBody: message,
        messageTitle: title,
      });
      setIsMessageModalOpen(true);
    }
  };

  const showAppliedMessage = () => {
    setIsModalApplySuccessOpen(true);
  };

  const showErrorApplyMessage = () => {
    showMessage({
      title: `Erro ao enviar candidatura`,
      message: `Houve um erro ao enviar a sua candidatura 😓`,
    });
  };

  const showErrorUpdateDeficiency = () => {
    showMessage({
      title: `Erro ao atualizar deficiência`,
      message: `Houve um erro ao atualizar a sua deficiência 😓`,
    });
  };

  const showConfirmEmailMessage = () => {
    showMessage({
      title: `Você precisa confirmar o seu email`,
      message: `Para se candatar a vagas você precisa confirmar o seu email. Acesse o email que você recebeu do Amarelinho Digital e confirme o seu email clicando no botão "CLIQUE AQUI"`,
    });
  };

  const verifyHasApplied = async () => {
    try {
      const applied = await UserService.hasApplied(job.id);
      setHasApplied(applied);
      if (applied) {
        void prefetchWhatsappMessageInstructions(job);
      }
    } catch (e) {}
  };

  const verifyCvProgress = async () => {
    setIsModalApplySuccessOpen(false);
    if (cvProgress && cvProgress.percentage <= 80) {
      setModalCvFillingOpen(true);
    }
  };

  const appliedSuccess = () => {
    AmplitudeService.track(`candidatura_enviada`, { vagaId: job.id });
    GtagService.candidaturaEnviada(job.id);
    showAppliedMessage();
    void prefetchWhatsappMessageInstructions(job);
    verifyHasApplied();
    UserService.getCvProgress().then((cvProgress) => setCvProgress(cvProgress));
  };

  const applyForThisJob = async () => {
    try {
      if (job.connekt) {
        const data = await JobService.applyWithConnekt(job.id);
        if (data.sucesso) {
          appliedSuccess();
          return true;
        } else {
          if (data.tratar) {
            if (data.tratar.includes(`não confirmou seu email`)) {
              showConfirmEmailMessage();
              return false;
            }
          }
          showErrorApplyMessage();
        }
      } else {
        const data = await JobService.applyFor(job);
        if (data.requestEmailValidation) {
          showConfirmEmailMessage();
        } else {
          appliedSuccess();
          return true;
        }
      }
    } catch (e) {
      showErrorApplyMessage();
    }
    return false;
  };

  const handleApplyForThisJob = async () => {
    setIsLoading(true);
    const [appliedWithSuccess, fetchedPersonalData] = await Promise.all([
      applyForThisJob(),
      job.pcd ? UserService.getPersonalData(UserService.getUserData()!.id) : Promise.resolve(null),
      DeficienciesService.fetchDeficiencies()
        .then((deficienciesList) => setDeficiencies(deficienciesList))
        .catch(() => {}),
    ]);
    setIsLoading(false);
    setIsConfirmModalOpen(false);
    if (appliedWithSuccess && job.pcd) {
      setPersonalData(fetchedPersonalData);
      if (!fetchedPersonalData?.deficiency?.id) {
        setIsModalCompleteCvPCDOpen(true);
      }
    }
  };

  const handleSavePersonalData = async () => {
    const newPersonalData = { ...personalData, deficiency: newDeficiency };
    try {
      setIsLoading(true);
      await UserService.savePersonalData(UserService.getUserData()!.id, newPersonalData);
      setIsLoading(false);
      setIsModalDeficienciesOpen(false);
    } catch (e) {
      showErrorUpdateDeficiency();
    }
  }

  const handleIWantToApply = () => {
    AmplitudeService.track('EV_BTN_QUERO_ME_CANDIDATAR', { jobId: job.id });
    if (!isLoggedIn) {
      GtagService.customEvent(`cadastro_vaga`, { type: job.slug, status: `candidatura-deslogado`, contact: `E-mail`, empresaId: job.idEmpresaAmaRH });

      router.push(
        trackedLink(`/login`, utms, {
          returnUrl: `/vagas/emprego/${job.slug}`,
        }),
      );
    } else {
      setIsConfirmModalOpen(true);
    }
  };

  const jobApplicationDisabled = job.status === `DISABLED` || job.expired;

  useEffect(() => {
    const syncHasApplied = async () => {
      if (jobApplicationDisabled) {
        return;
      }
      if (!isLoggedIn) {
        setHasApplied(null);
        return;
      }
      await verifyHasApplied();
    };

    void syncHasApplied();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const showRequirements =
    (job.experiences && job.experiences.length > 0) ||
    (!!job.academicLevel && !activities.includes(`Escolaridade mínima:`)) ||
    (job.languages && job.languages.length > 0);

  const withoutGeneralInformation = [
    `<strong>Requisitos para os candidatos`,
    `<strong>Principais atividades do cargo`,
  ].some((text) => activities.trim().startsWith(text));

  const updatedAt = new Date(job.updatedAt);
  const relativeDays = getRelativeDayFromToday(updatedAt);
  const relativeDaysLabel = getRelativeDayLabel(relativeDays);
  const relativeDaysText = relativeDays >= -1 ? `Atualizado ${relativeDaysLabel}` : `Há ${relativeDaysLabel}`;

  return (
    <Box>
      {!jobApplicationDisabled && Boolean(hasApplied) && <WaysOfContactList job={job} />}
      <Box className={styles.button_container}>
        {jobApplicationDisabled ? (
          <>
            <p className={styles.application_disabled}>Essa vaga não está ativa</p>
          </>
        ) : (
          <>
            {hasApplied ? (
              <button disabled className="btn btn-secondary btn-small">
                Candidatura enviada em:
                <br />
                {dateTimeFormat(new Date(hasApplied.createdAt))}
              </button>
            ) : (
              <>
                {linkToDetails ? (
                  <Link
                    href={`/vagas/emprego/${job.id}-vaga-de-${job.roleSlug}`}
                    className="btn btn-secondary btn-small"
                  >
                    Ver detalhes da vaga
                  </Link>
                ) : (
                  <button className="btn btn-secondary btn-small" onClick={handleIWantToApply}>
                    Quero me candidatar
                  </button>
                )}
              </>
            )}
          </>
        )}
        <SimpleModal open={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)}>
          <div className="d-flex flex-column">
            <div>
              <p className="title">Confirmação de candidatura</p>
              <p>
                Você deseja se candidatar para a vaga de <b>{formatJobTitle(job.title)}</b>
                {` `}
                {job.companyName.toLowerCase().includes(`empresa confidencial`)
                  ? `em uma ${job.companyName}`
                  : `na empresa ${job.companyName}`}
                ?
              </p>
            </div>

            <div className={applyForThisJobStyles.yes_or_no_buttons}>
              <button type="button" className="btn btn-small btn-outline" onClick={() => setIsConfirmModalOpen(false)}>
                Não
              </button>
              <button type="button" className="btn btn-small btn-secondary" onClick={handleApplyForThisJob}>
                Sim
              </button>
            </div>
          </div>
        </SimpleModal>
        {isLoading && <Loading />}

        <SimpleModal open={isModalApplySuccessOpen} onClose={() => verifyCvProgress()}>
          <p className="title">Candidatura enviada!</p>
          <p>Parabéns! A sua candidatura para a vaga de {formatJobTitle(job.title)} foi enviada 😊</p>
          {validWaysOfContact(job).length > 0 && (
            <>
              <p className="d-flex justify-content-center">
                <b>Formas de contato adicionais disponibilizadas pela empresa:</b>
              </p>
              <div className="d-flex justify-content-center">
                <WaysOfContactList job={job} />
              </div>
            </>
          )}
        </SimpleModal>

        <SimpleModal open={ isModalCvFillingOpen } onClose={ () => setModalCvFillingOpen(false) } style={ { maxWidth: `520px` } }>
          <p className="title">Capriche no currículo!</p>
          <p>
            O preenchimento do seu currículo é de {cvProgress?.percentage}%. Para ter mais chances de conseguir um
            emprego, complete-o com as informações abaixo e boa sorte! 🍀
          </p>
          <ul className="d-flex flex-wrap gap-1">
            {cvProgress?.missing.map((missingCvInfo) => (
              <li key={missingCvInfo}>
                <Link href={process.env.NEXT_PUBLIC_AREA_LOGADA + `/meu-curriculo`}>
                  <Chip color={cvProgress?.percentage <= 50 ? `danger` : `warning`} pointer>
                    {missingCvInfo}
                  </Chip>
                </Link>
              </li>
            ))}
          </ul>
          <a
            className="mt-4 btn btn-small btn-secondary btn-full"
            href={process.env.NEXT_PUBLIC_AREA_LOGADA + `/meu-curriculo`}
          >
            Terminar de preencher
          </a>
        </SimpleModal>

        <SimpleModal open={isMessageModalOpen} onClose={() => setIsMessageModalOpen(false)}>
          <p className="title">{messageTitle}</p>
          <p>{messageBody}</p>
        </SimpleModal>

        <SimpleModal open={isModalCompleteCvPCDOpen} onClose={() => setIsModalCompleteCvPCDOpen(false)}>
          <p className="title">Complementação do seu perfil PCD</p>
          <div>
            <p>
              Notamos que você se candidatou a uma vaga para Pessoa com Deficiência (PCD), mas o campo sobre deficiência
              ainda não foi preenchido no seu currículo.
            </p>
            <p>Gostaria de adicionar agora essa informação?</p>
          </div>
          <div style={{ display: 'flex', gap: '26px', justifyContent: 'center', justifyItems: 'center' }}>
            <button
              type="button"
              className="btn btn-small btn-outline"
              onClick={() => setIsModalCompleteCvPCDOpen(false)}
            >
              Não
            </button>
            <button type="button" className="btn btn-small btn-secondary" onClick={() => {setIsModalDeficienciesOpen(true); setIsModalCompleteCvPCDOpen(false);}}>
              Sim
            </button>
          </div>
        </SimpleModal>
        <SimpleModal open={isModalDeficienciesOpen} onClose={() => setIsModalDeficienciesOpen(false)} className={styles.modal_deficiency}>
          <p className="title">Atualizando deficiência</p>
          <div>
            <SelectField label="Deficiência" id="deficiency-select" name="deficiency" onChange={ (event) => {
              const deficiencyId = Number.parseInt(event.target.value);
              setNewDeficiency(deficiencies.find((def) => def.id === deficiencyId));
            } }>
              {deficiencies.map((deficiency) => (
                <option key={deficiency.id} value={deficiency.id}>
                  {deficiency.name}
                </option>
              ))}
            </SelectField>
          </div>
          <div style={{ display: 'flex', gap: '26px', justifyContent: 'center', justifyItems: 'center' }}>
            <button
              type="button"
              className="btn btn-small btn-outline"
              onClick={() => setIsModalDeficienciesOpen(false)}
            >
              Cancelar
            </button>
            <button type="button" className="btn btn-small btn-secondary" onClick={handleSavePersonalData}>
              Salvar
            </button>
          </div>
        </SimpleModal>
      </Box>
      <ContainerLinkDetails job={job} linkToDetails={linkToDetails}>
        <Box>
          {tagsBenefits && tagsBenefits.length > 0 && (
            <Box className={styles.benefits}>
              <h2>Empresa oferece</h2>
              <ul className={styles.benefits_list}>
                {tagsBenefits.map((benefit) => (
                  <li key={benefit} className="benefits-item">
                    <div className="img">
                      <Image
                        src={getBenefitIcon(benefit)}
                        alt={benefit}
                        width={42}
                        height={42}
                        style={{
                          objectFit: 'cover',
                        }}
                      />
                    </div>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </Box>
          )}
          {benefits && (
            <Box className={styles.benefits}>
              <h2>{tagsBenefits && tagsBenefits.length > 0 ? `Outros benefícios` : `Empresa oferece`}</h2>
              {benefits && <div dangerouslySetInnerHTML={{ __html: benefits }}></div>}
            </Box>
          )}

          <Box className={styles.details}>
            {job.companyRole && (
              <Box>
                <h2>Área de atuação:</h2>
                <p>{job.companyRole}</p>
              </Box>
            )}

            {salary != undefined && salary > 0 && (
              <Box className="salary">
                <h2>Salário:</h2>
                <p>R${monetaryMask(salary)}</p>
              </Box>
            )}

            <Box className="number-jobs">
              <h2>Quantidade de vagas:</h2>
              <p>
                {jobAmount} vaga{jobAmount > 1 && `s`}
              </p>
            </Box>

            {activities && (
              <Box className="activities">
                {!withoutGeneralInformation && <h2>Informações Gerais:</h2>}
                <div dangerouslySetInnerHTML={{ __html: maskActivities(activities) }}></div>
              </Box>
            )}

            {hiringContractTypes?.length > 0 && (
              <Box>
                <h2>Regime de contratação:</h2>
                <p>{hiringContractTypes.join(`, `)}</p>
              </Box>
            )}

            {weeklyWorkingHours && (
              <Box>
                <h2>Jornada de trabalho:</h2>
                <p>{weeklyWorkingHours} horas semanais</p>
              </Box>
            )}

            {extraInformation && (
              <Box className="extra-information">
                <h2>Mais informações sobre a vaga de emprego:</h2>
                <div dangerouslySetInnerHTML={{ __html: extraInformation }}></div>
              </Box>
            )}

            {showRequirements && (
              <Box className={styles.requirements}>
                <h2>Requisitos:</h2>
                <div>
                  <ul>
                    {job.experiences && job.experiences.length > 0 && (
                      <li className="experiences">
                        <p>{job.experiences.join(`, `)}</p>
                      </li>
                    )}
                    {!!job.academicLevel && !activities.includes(`Escolaridade mínima:`) && (
                      <li>
                        <p>Escolaridade mínima: {job.academicLevel}</p>
                      </li>
                    )}
                    {job.languages && job.languages.length > 0 && (
                      <li className="languages">
                        <p>
                          {job.languages.map(({ level, name }) => `Idioma: ${name} - Fluência: ${level}`).join(`; `)}
                        </p>
                      </li>
                    )}
                  </ul>
                </div>
              </Box>
            )}

            {!!companyPresentation && (
              <Box>
                <h2>Apresentação da empresa:</h2>
                <div dangerouslySetInnerHTML={{ __html: companyPresentation }}></div>
              </Box>
            ) }

          </Box>
          <p style={ { fontStyle: 'italic', textAlign: 'right', width: '100%' } }>Última atualização: {relativeDaysText}</p>
        </Box>
      </ContainerLinkDetails>
    </Box>
  );
}
